import {
  AppstoreOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  HistoryOutlined,
  PlusOutlined,
  ProfileOutlined,
  SearchOutlined,
  SendOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Empty, Input, Select, Spin, Tabs, Tooltip, Typography } from 'antd';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type WorkflowStatus, type WorkflowSummary, workflowsApi } from '@/api/workflows';
import { useRoles } from '@/auth/useRoles';
import { ConfirmPopover } from '@/components/ConfirmPopover';
import { ICON_BTN_PROPS as iconBtn } from '@/components/iconButton';
import { WorkflowCard } from '@/components/WorkflowCard';
import { useRememberedTab } from '@/hooks/useRememberedTab';
import { handleApiError } from '@/lib/handleApiError';
import { message } from '@/lib/message';
import { compareWorkflowsByName, workflowCardTitle } from '@/utils/workflowHelpers';

import { CloneWorkflowModal } from './CloneWorkflowModal';
import { CreateWorkflowModal } from './CreateWorkflowModal';
import {
  cardVersionLabel,
  type CardView,
  DASHBOARD_TABS,
  type DashboardTab,
  deriveCards,
  isDashboardTab,
  matchesTab,
} from './dashboardCards';
import { releaseRememberedLockIfAny } from './editorLock';
import { lockedByOtherTooltip } from './lockTooltip';
import { RejectionDetailsModal } from './RejectionDetailsModal';
import { getSubmitReadiness } from './submitGuard';

/**
 * Drag-wrap for a single Published card. Activation distance keeps button clicks
 * from triggering drags — dnd-kit fires drag once the pointer moves >8px.
 */
function SortablePublishedCard({
  id,
  disabled,
  children,
}: {
  id: string;
  /** When true, `useSortable` skips attaching drag listeners so the card
   *  behaves like a static block — no grab cursor, no drag preview, no
   *  keyboard drag. Used when the sort dropdown is on Last Updated or
   *  Name (A → Z), where a computed order dictates position and manual
   *  drag would fight the sort. */
  disabled: boolean;
  children: ReactNode;
}) {
  // `attributes` is deliberately NOT destructured/spread. dnd-kit puts
  // role="button" + tabIndex={0} + aria-roledescription="sortable" in there,
  // which would advertise a keyboard-drag affordance this dashboard does not
  // have — only a PointerSensor is registered (see `sensors` below), so there
  // is no KeyboardSensor to operate it. Spreading it cost us three things:
  // every card became a nested interactive control (it contains real buttons —
  // WCAG 4.1.2), every card added a dead tab stop, and screen readers
  // announced each as an operable button that no key press can move. Pointer
  // drag lives entirely in `listeners`, so dropping `attributes` loses nothing.
  // If keyboard reordering is ever wanted, add a KeyboardSensor and a dedicated
  // drag-handle button rather than re-spreading this onto the whole card.
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        // This wrapper sits between the grid cell and the card. The grid
        // stretches it, but a plain block wrapper would let the card keep its
        // natural height inside it — cards with no description or no submitted
        // date would render short. Flex column + the card's own `height: 100%`
        // hands the full cell height through.
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        // `cursor: default` when drag is disabled — otherwise the grab
        // affordance would mislead the user into thinking they can reorder.
        cursor: disabled ? 'default' : isDragging ? 'grabbing' : 'grab',
        zIndex: isDragging ? 2 : 'auto',
      }}
      {...(disabled ? {} : listeners)}
    >
      {children}
    </div>
  );
}

/**
 * The card's visible name. Also feeds the action buttons' accessible names:
 * a dashboard of N workflows otherwise renders N buttons all named "Edit", so
 * the name is what makes them distinguishable in a screen reader's button list.
 *
 * Delegates to the shared helper so this, the card's own title and the A → Z
 * sort can never disagree about what a workflow is called.
 */
const displayName = (w: WorkflowSummary, isFr: boolean) => workflowCardTitle(w, isFr);

const { Text } = Typography;

const ORIGINATOR_TAB_KEY = 'bmo-inflow.originator.activeTab';

// Stable reference so dnd-kit's useSensor doesn't see a "new" config object on
// every render. With this hoisted out, useSensors returns the same sensors array
// across renders and DndContext skips re-initialization.
const POINTER_SENSOR_OPTIONS = { activationConstraint: { distance: 8 } } as const;

/**
 * Sort mode for the visible card grid. `manual` preserves the originator's
 * drag-to-reorder (personal order); `lastChanged` and `nameAsc` override the
 * personal order with a computed order. Persisted per (oid, tab) in
 * localStorage — a user's Draft sort choice doesn't leak into their Published
 * tab, and different dev-login identities keep their preferences apart.
 */
type SortMode = 'manual' | 'lastChanged' | 'nameAsc';
const SORT_MODES: readonly SortMode[] = ['manual', 'lastChanged', 'nameAsc'];
const isSortMode = (v: unknown): v is SortMode =>
  typeof v === 'string' && (SORT_MODES as readonly string[]).includes(v);

export function WorkflowList() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const navigate = useNavigate();
  const { oid } = useRoles();
  const [items, setItems] = useState<WorkflowSummary[] | null>(null);
  // Personal display order per tab — local-only ("for personal use"), keyed by
  // the originator's oid + tab. Persisted in localStorage; never crosses the
  // wire. Canonical Order (the one BMO sees) is set by the Approver instead.
  const [personalOrder, setPersonalOrder] = useState<string[]>([]);
  // Sort mode (AC04) — per-tab preference. Manual (default) preserves the
  // personal drag-order; the other modes compute an order that overrides it.
  // Dragging while a non-manual sort is active is a no-op until the user
  // switches back to Manual.
  const [sortMode, setSortMode] = useState<SortMode>('manual');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<WorkflowSummary | null>(null);
  const [cloneTarget, setCloneTarget] = useState<WorkflowSummary | null>(null);
  const [rejectionTarget, setRejectionTarget] = useState<WorkflowSummary | null>(null);
  // Remember the active tab across refreshes — separate key per dashboard so the
  // Approver's selection doesn't bleed into the Originator's and vice versa. The
  // legacy 'Approved' value from the prior 4-tab layout maps to 'Published' so a
  // user who last visited the old Approved tab lands on Published, not Draft.
  const [filter, setFilterAndRemember] = useRememberedTab<DashboardTab>(
    ORIGINATOR_TAB_KEY,
    DASHBOARD_TABS,
    'Draft',
    (raw) => (raw === 'Approved' ? 'Published' : undefined),
  );
  // Search query for the dashboard. Substring match (case-insensitive) over
  // the visible name + description + app-name + owner-name. Filters the
  // active tab's cards AND drives the per-tab match-count badges so the
  // user can see which tabs have hits without flipping through each one.
  const [query, setQuery] = useState('');

  const reload = async () => {
    setLoading(true);
    try {
      setItems(await workflowsApi.list());
    } catch (err) {
      handleApiError(err, t, t('workflow.list.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // AC08 cleanup net: if the previous editor session left a workflow id in localStorage
    // (tab close, refresh, network blip on unmount, etc.), fire the release here and clear
    // it before reloading the list. Best-effort — failures fall through to the 14h TTL.
    void releaseRememberedLockIfAny().finally(() => {
      void reload();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the persisted personal order for the active tab. Fires whenever the
  // user switches tabs OR the identity changes (dev-login user switcher).
  useEffect(() => {
    if (!oid) {
      setPersonalOrder([]);
      return;
    }
    try {
      const raw = localStorage.getItem(`bmo-inflow.originator.order:${oid}:${filter}`);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      setPersonalOrder(
        Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [],
      );
    } catch {
      setPersonalOrder([]);
    }
  }, [oid, filter]);

  // Same restore pattern for the persisted sort mode — per (oid, tab).
  useEffect(() => {
    if (!oid) {
      setSortMode('manual');
      return;
    }
    try {
      const raw = localStorage.getItem(`bmo-inflow.originator.sort:${oid}:${filter}`);
      setSortMode(isSortMode(raw) ? raw : 'manual');
    } catch {
      setSortMode('manual');
    }
  }, [oid, filter]);

  const handleSortChange = (next: SortMode) => {
    setSortMode(next);
    try {
      localStorage.setItem(`bmo-inflow.originator.sort:${oid ?? 'anon'}:${filter}`, next);
    } catch {
      // localStorage quota / private mode — mode still applies for the session.
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await workflowsApi.remove(id);
      message.success(t('workflow.list.deleted'));
      await reload();
    } catch (err) {
      handleApiError(err, t, t('workflow.list.deleteFailed'));
    }
  };

  const handleSubmit = async (id: string) => {
    try {
      await workflowsApi.submit(id);
      message.success(t('workflow.list.sentToApprover'));
      await reload();
    } catch (err) {
      handleApiError(err, t, t('workflow.list.submitFailed'));
    }
  };

  const handleCancelPending = async (id: string) => {
    try {
      // Pass the current i18n language so the cancellation notice renders in
      // the originator's session locale (same shape as approve/reject).
      // Patch local state with the write-through response instead of doing a
      // reload() — /workflows list-all is a DDB Scan with eventually-consistent
      // reads, so an immediate refetch after the write occasionally returns
      // the stale (still-Pending) row and the card looks like it stuck.
      const updated = await workflowsApi.cancelPending(id, i18n.language);
      setItems((prev) => (prev === null ? prev : prev.map((w) => (w.id === id ? updated : w))));
      message.success(t('workflow.list.cancelPendingDone'));
    } catch (err) {
      handleApiError(err, t, t('workflow.list.cancelPendingFailed'));
    }
  };

  const statusLabel = (s: WorkflowStatus) => t(`workflow.statusLabels.${s}`);
  // Name / description / status-tag / lock chip / version chip rendering now lives
  // inside the shared <WorkflowCard /> component — see @/components/WorkflowCard.

  // dnd-kit sensors: PointerSensor with 8-px activation distance so a click on
  // an action button never accidentally starts a drag — only a deliberate
  // mouse-down + drag motion does. The options object is hoisted to module
  // scope above so useSensor sees a stable reference; useSensors then returns
  // a stable sensors array and DndContext doesn't re-initialize each render.
  const sensors = useSensors(useSensor(PointerSensor, POINTER_SENSOR_OPTIONS));

  // Per-originator-per-tab localStorage key. Stays local — Approver controls the
  // canonical Order that ships to BMO; this is purely the Originator's preferred
  // display arrangement of their own dashboard.
  const orderKey = (tab: DashboardTab) => `bmo-inflow.originator.order:${oid ?? 'anon'}:${tab}`;

  const handleDragEnd = (event: DragEndEvent) => {
    // Non-manual sort modes lock the order — dragging is a no-op until the
    // user switches Sort back to Manual. Keeps the visible order consistent
    // with what the sort dropdown says.
    if (sortMode !== 'manual') return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = visibleOrdered.map((c) => c.workflow.id);
    const oldIndex = ids.indexOf(active.id as string);
    const newIndex = ids.indexOf(over.id as string);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(ids, oldIndex, newIndex);
    setPersonalOrder(next);
    try {
      localStorage.setItem(orderKey(filter), JSON.stringify(next));
    } catch {
      // localStorage quota or private-mode — silent fallback to ephemeral
      // session order. The next page load will revert to BE's natural order.
    }
  };

  // A workflow can occupy two dashboard slots at once: an in-flight role (its
  // current Draft/Pending/Rejected version) and a published role (its still-live
  // last-approved version). v1-published + v2-draft therefore renders one card
  // in Draft and a separate one in PendingPublishing/Published — same template,
  // different lens. The "published" view splits further on isActive so the user
  // can see at a glance which Approved workflows the Approver has actually pushed
  // live to advisors.
  // Card derivation only changes when items changes — memoize so the 5-tab split
  // (flatMap + filter for each tab + filter for the active tab) doesn't re-run
  // on unrelated renders (e.g., a Submit autosave that just patched a card).
  const cards = useMemo<CardView[]>(() => deriveCards(items), [items]);
  const counts = useMemo(() => {
    return {
      Draft: cards.filter((c) => matchesTab(c, 'Draft')).length,
      Pending: cards.filter((c) => matchesTab(c, 'Pending')).length,
      PendingPublishing: cards.filter((c) => matchesTab(c, 'PendingPublishing')).length,
      Published: cards.filter((c) => matchesTab(c, 'Published')).length,
      Rejected: cards.filter((c) => matchesTab(c, 'Rejected')).length,
      visibleFor: (tab: DashboardTab) => cards.filter((c) => matchesTab(c, tab)),
    };
  }, [cards]);
  // Normalised search needle. Blank string disables the filter entirely so
  // the user can see all cards again without us re-running the substring
  // match across every render.
  const needle = query.trim().toLowerCase();
  const matchesQuery = useMemo(() => {
    if (!needle) return () => true;
    return (c: CardView) => {
      const w = c.workflow;
      // Most-likely-hit fields first so the OR short-circuits early.
      return (
        w.nameEn.toLowerCase().includes(needle) ||
        w.nameFr.toLowerCase().includes(needle) ||
        w.appName.toLowerCase().includes(needle) ||
        w.descriptionEn.toLowerCase().includes(needle) ||
        w.descriptionFr.toLowerCase().includes(needle) ||
        w.ownerName.toLowerCase().includes(needle)
      );
    };
  }, [needle]);
  // Per-tab match counts when the search is active. Drives the "(N)"
  // suffix on tab titles so the user can see at a glance which tabs hold
  // hits without flipping through each one.
  const matchCounts = useMemo(() => {
    if (!needle) return null;
    const m = {
      Draft: 0,
      Pending: 0,
      PendingPublishing: 0,
      Published: 0,
      Rejected: 0,
    } as Record<DashboardTab, number>;
    for (const tab of DASHBOARD_TABS) {
      m[tab] = counts.visibleFor(tab).filter(matchesQuery).length;
    }
    return m;
  }, [needle, counts, matchesQuery]);
  const visible = useMemo(
    () => counts.visibleFor(filter).filter(matchesQuery),
    [counts, filter, matchesQuery],
  );
  // Apply the effective order. Non-manual sort modes (AC04) override the
  // saved personal order — the computed sort takes precedence so dragging
  // is a no-op until Sort is switched back to Manual. In Manual mode the
  // original behaviour is preserved: personal-order ids come first (in the
  // saved order), then any new cards in BE-natural order.
  const visibleOrdered = useMemo(() => {
    if (sortMode === 'lastChanged') {
      return [...visible].sort(
        (a, b) =>
          new Date(b.workflow.updatedAt).getTime() - new Date(a.workflow.updatedAt).getTime(),
      );
    }
    if (sortMode === 'nameAsc') {
      return [...visible].sort((a, b) =>
        compareWorkflowsByName(a.workflow, b.workflow, i18n.language, isFr),
      );
    }
    if (personalOrder.length === 0) return visible;
    const byId = new Map(visible.map((c) => [c.workflow.id, c] as const));
    const sorted: typeof visible = [];
    for (const id of personalOrder) {
      const card = byId.get(id);
      if (card) {
        sorted.push(card);
        byId.delete(id);
      }
    }
    return [...sorted, ...byId.values()];
  }, [visible, personalOrder, sortMode, isFr, i18n.language]);
  // Submit-button readiness: pure but called per visible card. Pre-compute once
  // per render against the visible slice so the JSX map below just reads the
  // cached SubmitReadiness instead of re-running getSubmitReadiness in an IIFE
  // for every card.
  const readinessByCardKey = useMemo(() => {
    const map = new Map<string, ReturnType<typeof getSubmitReadiness>>();
    for (const { workflow: w, view } of visible) {
      if (view === 'published') continue;
      if (w.status !== 'Draft' && w.status !== 'Rejected') continue;
      if (!w.hasUnsubmittedChanges) continue;
      map.set(w.id, getSubmitReadiness(w, t));
    }
    return map;
  }, [visible, t]);
  // Tab labels carry the count badge. When the search is active the count
  // swaps to the match-count so the user can see which tabs have hits
  // (e.g. "Draft (2)") without scanning each one.
  const labelCount = (tab: DashboardTab) =>
    matchCounts ? `${matchCounts[tab]}` : `${counts[tab]}`;
  const tabItems = [
    { key: 'Draft', label: `${statusLabel('Draft')} (${labelCount('Draft')})` },
    {
      key: 'Pending',
      label: `${t('workflow.list.tabWaitingForApproval')} (${labelCount('Pending')})`,
    },
    {
      key: 'PendingPublishing',
      label: `${t('workflow.list.tabPendingPublishing')} (${labelCount('PendingPublishing')})`,
    },
    {
      key: 'Published',
      label: `${t('workflow.list.tabPublished')} (${labelCount('Published')})`,
    },
    { key: 'Rejected', label: `${statusLabel('Rejected')} (${labelCount('Rejected')})` },
  ];

  return (
    <div
      style={{
        background: '#ffffff',
        borderRadius: 12,
        padding: 24,
        border: '1px solid #ececee',
      }}
    >
      {/* Layout lives in global.css (`.wf-dash-header`) so the phone band can
          drop the buttons to their own row — an inline style object here would
          have to be fought with `!important`. */}
      <div className="wf-dash-header">
        <div>
          {/* The page's <h1> (WCAG 1.3.1 / 2.4.6). This dashboard is a top-level
              page so it cannot use PageHeader (which requires onBack); the
              level-3 type scale is pinned back on so semantics change and the
              design does not. Without it the document had no h1 at all, which
              breaks navigate-by-heading. */}
          <Typography.Title
            level={1}
            style={{ margin: 0, fontSize: 24, lineHeight: 1.35, fontWeight: 600 }}
          >
            {t('workflow.list.title')}
          </Typography.Title>
          <Text style={{ fontSize: 14, color: '#475467', display: 'block', marginTop: 2 }}>
            {t('workflow.list.subtitle')}
          </Text>
        </div>
        <div className="wf-dash-header__actions">
          <Button
            icon={<AppstoreOutlined />}
            onClick={() => navigate('/originator/custom-elements')}
          >
            {t('workflow.customElements.selectorTitle')}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            {t('workflow.list.createWorkflow')}
          </Button>
        </div>
      </div>

      {loading && items === null ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : (items ?? []).length === 0 ? (
        <Empty description={t('workflow.list.empty')} />
      ) : (
        <>
          <Tabs
            activeKey={filter}
            onChange={(k) => isDashboardTab(k) && setFilterAndRemember(k)}
            // Hook for the phone band, which lets AntD's tab-bar row wrap so
            // the sort + search slot drops under the tab strip instead of
            // running off the right edge.
            className="wf-dash-tabs"
            style={{ marginBottom: 12 }}
            items={tabItems}
            tabBarExtraContent={
              // AntD's `tabBarExtraContent` slot sits flush with the tab
              // strip's bottom underline — the `.wf-dash-tools` wrapper
              // carries a small bottom offset so the controls have breathing
              // room above the divider. Sort dropdown sits to the LEFT of the
              // search input so the reader's eye lands on "how the list is
              // ordered" before the free-text filter.
              <div className="wf-dash-tools">
                <Select<SortMode>
                  value={sortMode}
                  onChange={handleSortChange}
                  // AntD's Select renders an inner role="combobox" search input
                  // with no label of its own, so without this the sort control
                  // is announced as an unlabelled combobox (WCAG 4.1.2).
                  aria-label={t('workflow.list.sortLabel')}
                  // Width is in global.css — the phone band releases it.
                  className="wf-dash-tools__sort"
                  options={[
                    {
                      value: 'manual',
                      label: t('workflow.list.sortManual', { defaultValue: 'My order' }),
                    },
                    {
                      value: 'lastChanged',
                      label: t('workflow.list.sortLastChanged', {
                        defaultValue: 'Last updated',
                      }),
                    },
                    {
                      value: 'nameAsc',
                      label: t('workflow.list.sortNameAsc', { defaultValue: 'Name (A → Z)' }),
                    },
                  ]}
                />
                <Input
                  allowClear
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t('workflow.list.searchPlaceholder')}
                  // A placeholder is not an accessible name — it is not exposed
                  // as one and vanishes on input (WCAG 4.1.2 / 3.3.2).
                  aria-label={t('workflow.list.searchPlaceholder')}
                  prefix={<SearchOutlined aria-hidden style={{ color: '#6b7280' }} />}
                  // Width + pill radius are in global.css so the phone band
                  // can give the box its own full-width row.
                  className="wf-dash-tools__search"
                />
              </div>
            }
          />
          {visibleOrdered.length === 0 ? (
            // Two empty states:
            //   • Search active + zero matches → tell the user their query
            //     matched nothing on this tab AND offer a one-click Clear
            //     Search affordance (Search-requests AC05 "prompt to clear
            //     or adjust the search").
            //   • Search empty + tab empty → explain the tab is genuinely
            //     empty for this originator (e.g. "No Draft workflows yet").
            <Empty
              description={
                needle
                  ? t('workflow.list.noneMatchSearch', {
                      query: query.trim(),
                      defaultValue: 'No workflows match "{{query}}" on this tab.',
                    })
                  : t(`workflow.list.emptyTab.${filter}`, {
                      defaultValue: t('workflow.list.noneMatch', {
                        defaultValue: 'No workflows on this tab yet.',
                      }),
                    })
              }
            >
              {needle && (
                <Button onClick={() => setQuery('')}>
                  {t('workflow.list.clearSearch', { defaultValue: 'Clear search' })}
                </Button>
              )}
            </Empty>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                // All tabs are sortable now — Originator's personal display order
                // is per-tab in localStorage. Single-card tabs render a no-op
                // SortableContext (zero meaningful drags possible).
                items={visibleOrdered.map(({ workflow }) => workflow.id)}
                strategy={rectSortingStrategy}
              >
                <div
                  style={{
                    display: 'grid',
                    // `min(100%, 300px)`, not a bare 300px: on a screen whose
                    // content box is narrower than the track floor (a 320px
                    // phone, less the card's padding) a fixed floor makes the
                    // column wider than the grid and the page scrolls
                    // sideways. The floor still applies everywhere it fits.
                    gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))',
                    gap: 16,
                  }}
                >
                  {visibleOrdered.map(({ workflow: w, view }) => {
                    // 'published' view ignores any in-flight version — it represents
                    // the row advisors are currently using. 'inflight' is the
                    // working-copy view of whatever Draft/Pending/Rejected exists.
                    const isPublished = view === 'published';
                    // The staged re-approval. Labelled with the PENDING version,
                    // not the live one — the whole point of the card is to show
                    // the originator which version is waiting to go live, so
                    // borrowing currentVersionLabel here would render it as a
                    // duplicate of the Published card.
                    const isPendingApproval = view === 'pendingApproval';
                    const cardLabel = cardVersionLabel({ workflow: w, view });
                    // 'published' view splits on isActive: live → "Published" badge,
                    // not-yet-live → "Pending Publishing" badge. A staged approval
                    // is by definition not live.
                    const cardStatus = isPendingApproval
                      ? ('PendingPublishing' as const)
                      : isPublished
                        ? w.isActive
                          ? ('Published' as const)
                          : ('PendingPublishing' as const)
                        : w.status;
                    // Delete must never destroy a live baseline. Both views guard
                    // against it; for the in-flight view that also covers the
                    // "v2 draft on top of v1 published" case. We ALSO refuse to delete
                    // a workflow while another Originator is actively editing it — the
                    // BE returns 409 in that case (EnsureNoForeignLock), and the FE
                    // gate keeps the click from being attempted in the first place.
                    const wName = displayName(w, isFr);
                    // Every icon button below is icon-only. AntD names such a
                    // button after the icon glyph (aria-label="edit"), so without
                    // an explicit aria-label the whole action row announces as
                    // "edit", "delete", … in English, repeated once per card.
                    // `actionAria` pairs the localized action with the workflow.
                    const actionAria = (action: string) =>
                      t('workflow.list.actionAria', { action, name: wName });
                    const lockedByOther = !!w.lock && !w.lock.heldByCaller;
                    const cannotDelete =
                      isPublished ||
                      isPendingApproval ||
                      w.status === 'Pending' ||
                      w.currentVersionNumber > 0 ||
                      lockedByOther;
                    const card = (
                      <WorkflowCard
                        workflow={w}
                        versionLabel={cardLabel}
                        status={cardStatus}
                        onStatusClick={
                          cardStatus === 'Rejected' ? () => setRejectionTarget(w) : undefined
                        }
                        actions={
                          <>
                            <Tooltip
                              title={
                                isPublished
                                  ? t('workflow.builder.preview')
                                  : t('workflow.list.viewDetails')
                              }
                            >
                              <Button
                                {...iconBtn}
                                icon={isPublished ? <EyeOutlined /> : <ProfileOutlined />}
                                aria-label={actionAria(
                                  isPublished
                                    ? t('workflow.builder.preview')
                                    : t('workflow.list.viewDetails'),
                                )}
                                onClick={() =>
                                  // Published-view card → preview the live
                                  // published version's frozen content
                                  // (template detail would otherwise show the
                                  // in-flight v(N+1) draft's edits).
                                  // In-flight cards keep the detail page since
                                  // that's exactly what the originator is
                                  // editing.
                                  isPublished
                                    ? navigate(
                                        `/originator/workflows/${w.id}/preview?version=${w.currentVersionNumber}`,
                                      )
                                    : navigate(`/originator/workflows/${w.id}`)
                                }
                              />
                            </Tooltip>
                            {/* Published-only — the Eye button above is pinned to Preview for
                                this view, so dealer-code gating (and other metadata) needs its
                                own affordance here, version-pinned to the live baseline so it
                                never shows an in-flight unpublished draft's edits. */}
                            {isPublished && (
                              <Tooltip title={t('workflow.list.viewDetails')}>
                                <Button
                                  {...iconBtn}
                                  icon={<ProfileOutlined />}
                                  aria-label={actionAria(t('workflow.list.viewDetails'))}
                                  onClick={() =>
                                    navigate(
                                      `/originator/workflows/${w.id}/versions/${w.currentVersionNumber}`,
                                    )
                                  }
                                />
                              </Tooltip>
                            )}
                            <Tooltip title={t('workflow.list.versions')}>
                              <Button
                                {...iconBtn}
                                icon={<HistoryOutlined />}
                                aria-label={actionAria(t('workflow.list.versions'))}
                                onClick={() => navigate(`/originator/workflows/${w.id}/versions`)}
                              />
                            </Tooltip>
                            <Tooltip title={t('workflow.list.clone')}>
                              <Button
                                {...iconBtn}
                                icon={<CopyOutlined />}
                                aria-label={actionAria(t('workflow.list.clone'))}
                                onClick={() => setCloneTarget(w)}
                              />
                            </Tooltip>
                            <Tooltip
                              title={
                                // When someone else holds the lock, swap the tooltip to show why
                                // the button is disabled so the user understands instead of guessing.
                                lockedByOther
                                  ? lockedByOtherTooltip(w.lock, t)
                                  : t('workflow.list.edit')
                              }
                            >
                              <Button
                                {...iconBtn}
                                icon={<EditOutlined />}
                                aria-label={actionAria(t('workflow.list.edit'))}
                                disabled={
                                  isPublished ||
                                  (w.status !== 'Draft' && w.status !== 'Rejected') ||
                                  (w.lock != null && !w.lock.heldByCaller)
                                }
                                onClick={() => setEditTarget(w)}
                              />
                            </Tooltip>
                            {/* Approved workflows are live for advisors — deletion would break them.
                        Pending is in the approver's queue. An in-flight version on top of a
                        published baseline must be cancelled via the Versions page, not deleted
                        via the SoftDelete endpoint which removes the whole template. */}
                            <ConfirmPopover
                              title={t('workflow.list.deleteConfirmTitle')}
                              description={t('workflow.list.deleteConfirmDesc')}
                              onConfirm={() => handleDelete(w.id)}
                              disabled={cannotDelete}
                            >
                              <Tooltip
                                title={
                                  // Swap the tooltip when delete is blocked specifically by an
                                  // active editor — so the user knows it's not just "can't delete"
                                  // but "someone is editing right now, try again later".
                                  lockedByOther
                                    ? lockedByOtherTooltip(w.lock, t)
                                    : t('workflow.list.delete')
                                }
                              >
                                <Button
                                  {...iconBtn}
                                  danger
                                  icon={<DeleteOutlined />}
                                  aria-label={actionAria(t('workflow.list.delete'))}
                                  disabled={cannotDelete}
                                />
                              </Tooltip>
                            </ConfirmPopover>

                            {/* Withdraw a Pending submission — owner-only, Pending-only.
                                Delete/Cancel-pending story: the originator can pull the
                                request back before an approver decides. The BE also
                                rejects non-owners with 403, but hiding the icon keeps
                                a peer originator from seeing an action they can't
                                perform. Renders in the same right-anchored slot as
                                the Send button (they're mutually exclusive by status). */}
                            {w.status === 'Pending' && w.ownerOid === oid && !lockedByOther && (
                              <ConfirmPopover
                                title={t('workflow.list.cancelPendingConfirmTitle')}
                                description={t('workflow.list.cancelPendingConfirmDesc')}
                                onConfirm={() => handleCancelPending(w.id)}
                              >
                                <Tooltip title={t('workflow.list.cancelPending')}>
                                  <Button
                                    {...iconBtn}
                                    danger
                                    icon={<UndoOutlined />}
                                    aria-label={actionAria(t('workflow.list.cancelPending'))}
                                    style={{ ...iconBtn.style, marginLeft: 'auto' }}
                                  />
                                </Tooltip>
                              </ConfirmPopover>
                            )}

                            {!isPublished &&
                              (w.status === 'Draft' || w.status === 'Rejected') &&
                              w.hasUnsubmittedChanges &&
                              (() => {
                                // Pre-computed once per render against `visible` —
                                // see readinessByCardKey memo above the return JSX.
                                const readiness = readinessByCardKey.get(w.id) ?? { ok: true };
                                const submitDisabled = lockedByOther || !readiness.ok;
                                const lockReason = lockedByOther
                                  ? lockedByOtherTooltip(w.lock, t)
                                  : null;
                                const readinessReason = !readiness.ok ? readiness.reason : null;
                                const positive =
                                  w.status === 'Rejected'
                                    ? t('workflow.list.resubmitToApprover')
                                    : t('workflow.list.sendToApprover');
                                return (
                                  <ConfirmPopover
                                    title={t('workflow.list.sendConfirmTitle')}
                                    description={t('workflow.list.sendConfirmDesc')}
                                    onConfirm={() => handleSubmit(w.id)}
                                    disabled={submitDisabled}
                                  >
                                    <Tooltip title={lockReason ?? readinessReason ?? positive}>
                                      <Button
                                        {...iconBtn}
                                        type="primary"
                                        ghost
                                        disabled={submitDisabled}
                                        icon={<SendOutlined />}
                                        aria-label={actionAria(positive)}
                                        style={{ ...iconBtn.style, marginLeft: 'auto' }}
                                      />
                                    </Tooltip>
                                  </ConfirmPopover>
                                );
                              })()}

                            {/* The Active toggle gates advisor access to the smart form. It is
                            now Approver-only at the BE (deactivating a live workflow affects
                            production) so we hide it from the Originator dashboard — clicks
                            would 403. The Approver-side UI for it lands in the
                            ApproverPublishedList component (see /approver/dashboard). */}
                          </>
                        }
                      />
                    );
                    // Every card on every tab is sortable now — local-only
                    // personal order. The wrapper name predates the spec change
                    // (was Published-only); it's just "this card is draggable".
                    return (
                      <SortablePublishedCard
                        key={`${w.id}-${view}`}
                        id={w.id}
                        // Disable drag when the computed sort dictates position.
                        // Manual is the only mode where personal drag-order wins.
                        disabled={sortMode !== 'manual'}
                      >
                        {card}
                      </SortablePublishedCard>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </>
      )}

      <CreateWorkflowModal
        open={createOpen || editTarget !== null}
        editing={editTarget}
        onClose={() => {
          setCreateOpen(false);
          setEditTarget(null);
        }}
        onSaved={(saved) => {
          // Patch local state immediately rather than refetching from the
          // eventually-consistent byOwner GSI — that way the resubmit button
          // reflects the freshly-flipped hasUnsubmittedChanges flag right away.
          setItems((prev) => {
            if (prev === null) return prev;
            const idx = prev.findIndex((w) => w.id === saved.id);
            return idx >= 0 ? prev.map((w) => (w.id === saved.id ? saved : w)) : [saved, ...prev];
          });
        }}
      />

      <CloneWorkflowModal
        source={cloneTarget}
        existingAppNames={(items ?? []).map((w) => w.appName)}
        onClose={() => setCloneTarget(null)}
        onCloned={(saved) => setItems((prev) => (prev === null ? prev : [saved, ...prev]))}
      />

      <RejectionDetailsModal workflow={rejectionTarget} onClose={() => setRejectionTarget(null)} />
    </div>
  );
}
